import IMask from "imask";
import { isString } from "imask/esm/core/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, DropdownMenu, TextField as RTextField } from "reshaped";

import {
    formatInternationalPhoneNumber,
    getPhoneZoneIMask,
    parsePhoneNumber,
} from "@mpa/toolbox/phone/index";
import { phoneZones } from "@mpa/toolbox/phone/phoneZones";
import {
    type PhoneZone,
    phoneZoneCategories,
    phoneZoneCetagoriesLabels,
} from "@mpa/toolbox/phone/types";

import { Field } from "./Field";
import {
    type AnyFieldProps,
    useFieldDirtyness,
    useFieldId,
    useFieldImperativeHandle,
    useValidity,
    validationFn,
} from "./utilities";

export type PhoneFieldProps = AnyFieldProps<string>;

export default function PhoneField(props: PhoneFieldProps) {
    const { onChange } = props;

    const id = useFieldId(props);
    const { isDirty, setDirty } = useFieldDirtyness(props);
    const inputRef = useRef<HTMLInputElement>(null);

    // Value

    const value = props.value || "";

    // Validity
    const validator = useMemo(
        () =>
            validationFn<string>(`phone-${id}`, (value) => {
                const parsed = parsePhoneNumber(value);
                if (!parsed) throw new Error("Phone number is not valid");
                if (!parsed.valid) throw new Error("Phone number is not valid");
            }),
        [id],
    );
    const validity = useValidity(props, [validator]);

    // Value derived from props

    const parsedValue = useMemo(() => {
        if (value === "") return null;
        if (!isString(value)) return value;
        return parsePhoneNumber(value);
    }, [value]);

    // Values in input
    const [phoneZone, setPhoneZone] = useState<PhoneZone>(
        parsedValue ? parsedValue.phoneZone : (phoneZones[0] as PhoneZone),
    );
    const [inputValue, setInputValue] = useState<string>(parsedValue ? parsedValue.local : "");

    // Sync input => caller
    useEffect(() => {
        const formatted = formatInternationalPhoneNumber(inputValue, phoneZone);
        if (formatted.valid && formatted.international !== value) onChange(formatted.international);
    }, [inputValue, phoneZone, onChange]);

    // Sync caller => input

    const [prevParsedValue, setPrevParsedValue] = useState(parsedValue);
    if (parsedValue && prevParsedValue) {
        if (
            parsedValue.phoneZone !== prevParsedValue.phoneZone ||
            parsedValue.local !== prevParsedValue.local
        ) {
            setPhoneZone(parsedValue.phoneZone);
            setInputValue(parsedValue.local);
            setPrevParsedValue(parsedValue);
        }
    }

    // Mask
    useEffect(() => {
        if (!inputRef.current) return;
        const mask = IMask(inputRef.current, { mask: getPhoneZoneIMask(phoneZone) });
        return () => mask.destroy();
    }, [phoneZone]);

    // Ref
    useFieldImperativeHandle(props, { validity, setDirty, inputRef });

    // On blur

    const onBlur = useCallback(() => {
        setDirty();
        const formatted = formatInternationalPhoneNumber(inputValue, phoneZone);
        onChange(formatted.international);
    }, [inputValue, phoneZone, onChange, setDirty]);

    return (
        <Field {...props} isDirty={isDirty} validity={validity}>
            <RTextField
                name={id}
                value={inputValue}
                onChange={(e) => setInputValue(e.value)}
                inputAttributes={{ onBlur: onBlur, ref: inputRef }}
                placeholder={phoneZone.mask}
                startSlot={
                    <DropdownMenu>
                        <DropdownMenu.Trigger>
                            {(a) => (
                                <Button attributes={a} size="small" variant="ghost">
                                    {phoneZone.emoji} {phoneZone.prefix}
                                </Button>
                            )}
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content>
                            {phoneZoneCategories.map((c) => {
                                const categoryPhoneZones = phoneZones.filter(
                                    (z) => z.category === c,
                                );
                                return (
                                    <DropdownMenu.SubMenu>
                                        <DropdownMenu.SubTrigger>
                                            {phoneZoneCetagoriesLabels[c]}
                                        </DropdownMenu.SubTrigger>
                                        <DropdownMenu.Content>
                                            {categoryPhoneZones.map((z) => (
                                                <DropdownMenu.Item
                                                    key={`${z.prefix}-${z.label}`}
                                                    onClick={() => setPhoneZone(z)}
                                                >
                                                    {z.emoji} {z.label} ({z.prefix})
                                                </DropdownMenu.Item>
                                            ))}
                                        </DropdownMenu.Content>
                                    </DropdownMenu.SubMenu>
                                );
                            })}
                        </DropdownMenu.Content>
                    </DropdownMenu>
                }
            />
        </Field>
    );
}
